import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppAssistApi, AppAssistTurn } from "./AppAssistApi";
import { AppAssistPiece } from "./AppAssistPanel";
import { AppAssistView } from "./AppAssistView";
import { AppMoney } from "./AppMoney";

// AppAskAllRules - "Ask about everyone" (owner-approved #6, 2026-09-27): ONE question across ALL the owner's
// one-to-one chats - "Who have I promised money to?", "Who's waiting on me?", "Who did I talk to about the
// kitchen?". The body the page sends to `/assist {action:"all"}`, the contact-link token both halves share,
// and the fixed markup of the box under the search. `AppAskAll` is the act; `AssistAllRules` the service.
//
// ⚠ RULES ONLY - NO `window`, NO `XMLHttpRequest`, NO `document`. The Bridge suite imports this file to pin the
// token and the body against the service (`AskAllContract.test.ts`) - the `AppConnect` trap.
//
// ==================================================================================================
// !! ⛔ A LINK IS ONLY EVER ONE OF THE OWNER'S OWN CHATS - NEVER A FREE ADDRESS
// ==================================================================================================
//
// The model names a contact by a short id from its context (`[[c3]]`). The SERVICE turns a known id into
// `[[c:<hex of the index href>]]` and drops every other token (`AssistAllRules.Linked`); the PAGE decodes the
// hex and draws a link ONLY when the href is a row of its own loaded index (`Pieces`). The address is then
// `#c/<encoded href>` - the same route a card uses - built here, never taken from model text. Hex because it is
// Markdown-inert: an href with `_` or `*` would otherwise be read as emphasis by `AppAssistRules.Spans`.
//
// ⛔ NO DRAFT, NO SEND: this panel is not about one chat, so it has no draft card at all (`IAssistMode`).

/** A contact the page can link to: the index row's raw href and its title. */
export class AppAskAllLink {
    public Href: string = "";
    public Title: string = "";
}

export class AppAskAllRules {
    /** ⛔ Must match `AssistRules.AllAction` - pinned by `AskAllContract.test.ts`. */
    public static readonly Action: string = "all";
    /** The warm-up's `mode` for this panel's system prompt. ⛔ Must match `AssistRules.AllMode`. */
    public static readonly WarmMode: string = "all";

    public static readonly RootId: string = "wac-askall";
    public static readonly OpenId: string = "wac-askall-open";
    public static readonly PanelId: string = "wac-askall-panel";
    public static readonly CloseId: string = "wac-askall-close";
    public static readonly LogId: string = "wac-askall-log";
    public static readonly ChipsId: string = "wac-askall-chips";
    public static readonly FormId: string = "wac-askall-form";
    public static readonly BoxId: string = "wac-askall-q";
    /** The visible words of a link to a chat; its accessible name carries the contact's name. */
    public static readonly LinkText: string = "open chat";

    /** A resolved token: `[[c:` + lower-case hex of the href's UTF-8 + `]]`. */
    public static readonly TokenPattern: RegExp = /\[\[c:([0-9a-f]{2,2048})\]\]/;
    /** Any token, resolved or the model's raw `[[c12]]` - global, for `Pieces`. */
    private static readonly AnyToken: RegExp = /\[\[c(?::([0-9a-f]{2,2048})|(\d{1,5}))\]\]/g;
    /** A token cut off at the end of streamed text ("[[c1", "[[c:4a") - never shown while it grows. */
    private static readonly Partial: RegExp = /\[(\[(c(\d{0,5}|:[0-9a-f]{0,2048})\]?)?)?$/;

    /**
     * The ready-made questions. ⚠ A chip carries its INDEX into this list, read back against the same list.
     * ⚠ Appended, never inserted.
     */
    public static readonly Presets: string[] = [
        "Who's waiting on me?",
        "Who have I promised money to?",
        "What's still open with everyone?",
        "Who haven't I been in touch with lately?",
    ];

    /** The token for `Href`. "" for an empty href. */
    public static Token = (Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Href).length === 0) { return ""; }
            const Bytes: Uint8Array = new TextEncoder().encode(nz(Href));
            const Out: string[] = [];
            for (const One of Bytes) { Out.push((One + 256).toString(16).substring(1)); }
            return "[[c:" + Out.join("") + "]]";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllRules.Token");
            return "";
        }
    };

    /**
     * The href a token's hex spells, or "" when it is not whole UTF-8 (then no link is drawn).
     * DOCUMENTED EXCEPTION: pure decode. ERROR-SUPPRESSED-JUSTIFIED: invalid UTF-8 throws in the fatal decoder,
     * and "" IS the defined answer - the token is then dropped, never drawn as a link.
     */
    public static HrefOf = (Hex: string): string => {
        try {
            const Text: string = nz(Hex);
            if (Text.length % 2 !== 0 || /^[0-9a-f]*$/.test(Text) === false) { return ""; }
            const Bytes: Uint8Array = new Uint8Array(Text.length / 2);
            for (let At: number = 0; At < Bytes.length; At = At + 1) { Bytes[At] = parseInt(Text.substring(At * 2, At * 2 + 2), 16); }
            return new TextDecoder("utf-8", { fatal: true }).decode(Bytes);
        } catch (e) {
            return "";
        }
    };

    /**
     * One run of answer text as pieces: plain text, and a link for each resolved token whose href is one of
     * `Links` (the page's own index rows). ⛔ Anything else - a raw `[[c12]]` (a stream before the service
     * resolved it), a token for a chat the page does not have, the half-arrived token at the end of a stream -
     * is DROPPED, never drawn as text or as a link.
     */
    public static Pieces = (Text: string, Links: AppAskAllLink[]): AppAssistPiece[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Src: string = nz(Text).replace(AppAskAllRules.Partial, "");
            const Out: AppAssistPiece[] = [];
            const Find: RegExp = new RegExp(AppAskAllRules.AnyToken.source, "g");
            let From: number = 0;
            let Hit: RegExpExecArray | null = Find.exec(Src);
            while (Hit !== null) {
                AppAskAllRules.PushText(Out, Src.substring(From, Hit.index));
                if (Hit[1] !== undefined) { AppAskAllRules.PushLink(Out, AppAskAllRules.HrefOf(Hit[1]), Links); }
                From = Hit.index + Hit[0].length;
                Hit = Find.exec(Src);
            }
            AppAskAllRules.PushText(Out, Src.substring(From));
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllRules.Pieces");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: appends a text piece when there is text. ERROR-SUPPRESSED-JUSTIFIED: pure, guarded caller. */
    private static PushText = (Out: AppAssistPiece[], Text: string): void => {
        if (Text.length === 0) { return; }
        const Piece: AppAssistPiece = new AppAssistPiece();
        Piece.Text = Text;
        Out.push(Piece);
    };

    /** A link piece for `Href` when it is exactly one of `Links`; nothing otherwise. */
    private static PushLink = (Out: AppAssistPiece[], Href: string, Links: AppAskAllLink[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Href.length === 0) { return; }
            for (const One of Links) {
                if (One.Href !== Href) { continue; }
                const Piece: AppAssistPiece = new AppAssistPiece();
                Piece.Text = AppAskAllRules.LinkText;
                Piece.Href = One.Href;
                Piece.Label = nz(One.Title);
                Out.push(Piece);
                return;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllRules.PushLink");
        }
    };

    /**
     * The body, or "" when it cannot be asked (a signed-out browser). ⚠ `page` is "" - the question is about
     * no one chat; the service resolves every contact through its own index. Turns as `AppAssistApi.Body` cuts them.
     */
    public static Body = (Phone: string, Key: string, Ask: string, Turns: AppAssistTurn[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim() === "" || nz(Key).trim() === "") { return ""; }
            // ⚠ THE SAME CUT AS THE PANEL'S BODY: built with a placeholder page, then the page is emptied.
            const Base: string = AppAssistApi.Body(Phone, Key, "all", Ask, Turns);
            if (Base.length === 0) { return ""; }
            const Doc: Record<string, unknown> = JSON.parse(Base) as Record<string, unknown>;
            Doc.page = "";
            Doc.action = AppAskAllRules.Action;
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllRules.Body");
            return "";
        }
    };

    /** The warm-up for THIS panel's prompt (owner only at the service). "" when signed out. */
    public static WarmBody = (Phone: string, Key: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim() === "" || nz(Key).trim() === "") { return ""; }
            return JSON.stringify({ number: nz(Phone).trim(), key: nz(Key).trim(), page: "", action: "warm",
                mode: AppAskAllRules.WarmMode });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllRules.WarmBody");
            return "";
        }
    };

    /** The visually hidden description the question box points at. */
    public static readonly IntroId: string = "wac-askall-intro";

    /**
     * ⭐ A TOOLTIP ON THE BUTTON AND THE HEADING + THE BOX'S SCREEN-READER DESCRIPTION - never a paragraph in the
     * panel (owner, 2026-09-28: explanations belong in a tooltip). ⚠ Constant text with no quote or `<`, so it goes
     * into an attribute as is.
     * DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw.
     */
    public static Intro = (): string => {
        return "Ask one question about all your one-to-one chats - who is waiting on you, who you owe money,"
            + " who you talked to about something. Names in the answer link to their chats, where you can write to them.";
    };

    /** DOCUMENTED EXCEPTION: pure constant sentence - no input, cannot throw. */
    public static NoSendWhy = (): string => {
        return "Nothing is sent from here - open the person's chat to write to them.";
    };

    /**
     * The box under the search: the "Ask about everyone" button and its panel (hidden until opened). "" when
     * the browser is not the owner's (the service would refuse anyway).
     * ⚠ Only constant markup is built here - every question and answer is drawn by `AppAssistPanel` as text.
     */
    public static Html = (Open: boolean, Money: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Glyph: string = "<span class=\"spark\" aria-hidden=\"true\">" + AppAssistView.Glyph + "</span>";
            let Hidden: string = " hidden";
            if (Open === true) { Hidden = ""; }
            return "<div class=\"askall\" id=\"" + AppAskAllRules.RootId + "\">"
                + "<button type=\"button\" class=\"askallbtn btn secondary\" id=\"" + AppAskAllRules.OpenId + "\" aria-controls=\""
                + AppAskAllRules.PanelId + "\" aria-expanded=\"" + String(Open === true) + "\" title=\""
                + AppAskAllRules.Intro() + "\">" + Glyph
                + "Ask about everyone</button>"
                + "<section class=\"askallpanel\" id=\"" + AppAskAllRules.PanelId + "\" aria-label=\"Ask about everyone\"" + Hidden + ">"
                + "<header class=\"ahead\">" + Glyph + "<h2 title=\"" + AppAskAllRules.Intro() + "\">Ask about everyone</h2><button type=\"button\" class=\"aclose btn ghost icon sm\" id=\""
                + AppAskAllRules.CloseId + "\" aria-label=\"Close\" title=\"Close\">" + AppAssistView.CloseGlyph + "</button></header>"
                + "<div class=\"alog\" id=\"" + AppAskAllRules.LogId + "\" aria-live=\"polite\"></div>"
                + AppAskAllRules.Chips(Money)
                + "<form class=\"aask\" id=\"" + AppAskAllRules.FormId + "\"><textarea id=\"" + AppAskAllRules.BoxId
                + "\" rows=\"1\" aria-label=\"Ask about all your chats\" autocomplete=\"off\""
                + " aria-describedby=\"" + AppAskAllRules.IntroId + "\"></textarea>"
                + "<span class=\"sr\" id=\"" + AppAskAllRules.IntroId + "\">" + AppAskAllRules.Intro() + "</span>"
                + "<button type=\"submit\" class=\"ago btn\">Ask</button></form></section></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllRules.Html");
            return "";
        }
    };

    /**
     * The question for chip `At`, or "" for none. `Presets.length` is the "Revenue" chip, and only with money on.
     * DOCUMENTED EXCEPTION: pure lookup. ERROR-SUPPRESSED-JUSTIFIED: "" (ask nothing) IS the safe answer.
     */
    public static QuestionAt = (At: number, Money: boolean): string => {
        if (isNaN(At) === true || At < 0) { return ""; }
        if (At < AppAskAllRules.Presets.length) { return AppAskAllRules.Presets[At]; }
        if (At === AppAskAllRules.Presets.length && Money === true) { return AppMoney.AskPreset; }
        return "";
    };

    /** The chips - each carries its INDEX into `Presets` (`AppAssistView.AskAttr`), never the question. */
    private static Chips = (Money: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Out: string[] = ["<div class=\"chips\" id=\"" + AppAskAllRules.ChipsId
                + "\" role=\"toolbar\" aria-label=\"Ask about everyone\">"];
            for (let At: number = 0; At < AppAskAllRules.Presets.length; At = At + 1) {
                Out.push("<button type=\"button\" class=\"chip\" " + AppAssistView.AskAttr + "=\"" + String(At) + "\">"
                    + kHTMLCore.EscapeHtml(AppAskAllRules.Presets[At]) + "</button>");
            }
            // ⭐ "Revenue" - index `Presets.length`, asked as `AppMoney.AskPreset` (`QuestionAt`); only with money on.
            if (Money === true) {
                Out.push("<button type=\"button\" class=\"chip\" " + AppAssistView.AskAttr + "=\""
                    + String(AppAskAllRules.Presets.length) + "\" title=\"" + kHTMLCore.EscapeAttr(AppMoney.AskPreset)
                    + "\">Revenue</button>");
            }
            Out.push("</div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAskAllRules.Chips");
            return "";
        }
    };
}
